import { forwardRef } from "react";

/**
 * `AgentIncognitoIcon` — the agent's glyph: fedora + glasses + moustache, the
 * classic "incognito / secret agent" mark (operator direction 2026-07-21,
 * replacing lucide's `Bot`).
 *
 * Custom rather than a lucide import because lucide has no equivalent — the
 * nearest candidates are `VenetianMask` (a masquerade mask) and `Drama`
 * (theatre masks), and neither reads as this. Custom rail glyphs are already
 * an established pattern here (`LexiunMarkRailIcon` wraps the brand mark).
 *
 * Construction (variant H, chosen 2026-07-21): wide flat brim + solid crown,
 * the face left as NEGATIVE SPACE with two angled slashes for eyes, and a V
 * collar so it reads as a figure rather than a hat. Filled shapes on a 24×24
 * viewBox, not lucide-style strokes — at rail size (18–22px) thin strokes on a
 * brim disappear, whereas high-contrast gaps hold.
 *
 * This is OUR artwork in a shared visual idiom (the fedora/incognito trope also
 * used by Chrome's incognito mark and Font Awesome's `user-secret`). It is
 * deliberately NOT a vendor logo: AgentMail — whose console inspired the look —
 * is an actual vendor in this stack, so shipping their brand mark as our own
 * feature icon would read as endorsement.
 *
 * Exposed as a `forwardRef` component so it is structurally assignable to
 * lucide's `LucideIcon` type — tables that declare `icon: LucideIcon` (the
 * settings tab registry, the nav rail) accept it with no wrapper and no cast.
 * `strokeWidth` is accepted and ignored: the glyph is filled.
 */

/**
 * Mirrors lucide's prop surface (including `strokeWidth: string | number`, which
 * is wider than it looks) so the component is assignable to `LucideIcon`.
 * Extra SVG props pass through.
 */
export interface AgentIncognitoIconProps
  extends Omit<React.SVGProps<SVGSVGElement>, "ref"> {
  size?: number | string;
  /** Accepted for lucide call-site compatibility; the glyph is filled. */
  strokeWidth?: number | string;
  absoluteStrokeWidth?: boolean;
}

export const AgentIncognitoIcon = forwardRef<
  SVGSVGElement,
  AgentIncognitoIconProps
>(function AgentIncognitoIcon(
  { size = 24, className, strokeWidth: _sw, absoluteStrokeWidth: _asw, ...rest },
  ref,
) {
  return (
    <svg
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      {...rest}
    >
      {/* Crown. */}
      <path
        d="M8.15 8.35c.1-2.55.5-4.15 1.2-4.85.62-.62 1.5-.93 2.65-.93s2.03.31 2.65.93c.7.7 1.1 2.3 1.2 4.85H8.15Z"
        fill="currentColor"
      />
      {/* Brim — wide and flat; this is the silhouette's signature. */}
      <path
        d="M1.7 9.75c0-1.1 1.65-1.6 10.3-1.6s10.3.5 10.3 1.6c0 1.2-3.65 1.8-10.3 1.8S1.7 10.95 1.7 9.75Z"
        fill="currentColor"
      />
      {/* Eyes — angled slashes. The FACE is negative space: high-contrast gaps
          survive small sizes where thin positive detail turns to mush. */}
      <path
        d="M7.35 12.75c1.2.25 2.25.72 3.15 1.4l-.72 1.62c-1.05-.72-2.2-1.2-3.45-1.45l1.02-1.57Z"
        fill="currentColor"
      />
      <path
        d="M16.65 12.75c-1.2.25-2.25.72-3.15 1.4l.72 1.62c1.05-.72 2.2-1.2 3.45-1.45l-1.02-1.57Z"
        fill="currentColor"
      />
      {/* Collar — the V that turns a hat into a figure. */}
      <path
        d="M12 21.9 5.9 17.3c.52-.7 1.18-1.28 1.95-1.72L12 19.05l4.15-3.47c.77.44 1.43 1.02 1.95 1.72L12 21.9Z"
        fill="currentColor"
      />
    </svg>
  );
});
